import type { ReactElement, ReactNode } from 'react';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ButtonVariant } from '@/constants';
import type { ButtonVariantType } from '@/types';
import * as Styled from './Button.styled';

type ButtonProps = {
  children: ReactNode;
  handler?: () => void;
  icon?: ReactElement;
  slot?: string;
  width?: number;
  variant?: ButtonVariantType | ButtonVariantType[];
} & (
  | {
      isLink: true;
      href: string;
      isPending?: never;
      type?: never;
    }
  | {
      isLink?: false;
      href?: never;
      isPending?: boolean;
      type?: 'button' | 'reset' | 'submit';
    }
);

export const Button = ({
  children,
  handler,
  href,
  icon,
  isLink = false,
  isPending = false,
  type,
  variant = ButtonVariant.Primary,
  ...deferred
}: ButtonProps) => {
  const props = {
    $isFullWidth: variant.includes(ButtonVariant.FullWidth),
    $isSecondary: variant.includes(ButtonVariant.Secondary),
    ...(isLink ? { href } : {}),
    ...(isLink ? {} : { isPending }),
    onPress: handler,
    ...(isLink ? {} : { type }),
    ...deferred,
  };

  return isLink ? (
    <Styled.Link {...props}>
      <>
        {icon ? <Styled.Icon>{icon}</Styled.Icon> : null}
        {children}
      </>
    </Styled.Link>
  ) : (
    <Styled.Button {...props}>
      {({ isPending }) => (
        <>
          {isPending ? <LoadingSpinner /> : null}
          <Styled.ButtonText $isPending={isPending}>
            {icon ? <Styled.Icon>{icon}</Styled.Icon> : null}
            {children}
          </Styled.ButtonText>
        </>
      )}
    </Styled.Button>
  );
};
